/* Shared non-visual helpers for the Supply-Side module.
   Re-exports the dummy-data formatters and adds download / CSV / XLSX export and
   small presentation helpers (agreement cells, service-type chips, classnames). */
import * as XLSX from 'xlsx'
import {inr, money, fmtDate, fmtDateTime, relLabel, initials, TODAY} from '../dummyData/format'
import {agreementShort, agreementLabel} from '../dummyData/agreements'
import {SERVICE_TYPES_BY_CODE} from '../dummyData/serviceTypes'

export {inr, money, fmtDate, fmtDateTime, relLabel, initials, TODAY}

/* classnames join */
export const cx = (...parts) => parts.filter(Boolean).join(' ')

/* Trigger a browser download of text/blob content. */
export function download(name, content, mime) {
  const blob =
    content instanceof Blob ? content : new Blob([content], {type: mime || 'text/csv;charset=utf-8'})
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

const stripTags = (v) =>
  String(v == null ? '' : v)
    .replace(/<[^>]*>/g, '')
    .replace(/&[a-z]+;/g, ' ')
    .trim()

/* rows + cols [{label, value(r)|key}] → CSV string */
export function toCsv(rows, cols) {
  const cell = (v) => {
    v = String(v == null ? '' : v)
    return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v
  }
  const lines = [cols.map((c) => cell(c.label)).join(',')]
  rows.forEach((r) => {
    lines.push(cols.map((c) => cell(stripTags(c.value ? c.value(r) : r[c.key]))).join(','))
  })
  return lines.join('\n')
}

/* Export a filtered set to .xlsx ([name]_YYYY-MM-DD.xlsx) using SheetJS. */
export function exportTable(rows, cols, name) {
  const fname = (name || 'export') + '_' + TODAY.toISOString().slice(0, 10)
  const aoa = [cols.map((c) => c.label)]
  rows.forEach((r) => aoa.push(cols.map((c) => stripTags(c.value ? c.value(r) : r[c.key]))))
  const ws = XLSX.utils.aoa_to_sheet(aoa)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, (name || 'Export').slice(0, 31))
  XLSX.writeFile(wb, fname + '.xlsx')
  return rows.length
}

/* Agreement label resolvers (re-exported for cells) */
export {agreementShort, agreementLabel}

/* service-type chip data — returns [{code, tip}] capped at `max` plus an overflow note */
export function serviceTypeChipData(codes, max = 3) {
  codes = codes || []
  const shown = codes.slice(0, max).map((c) => {
    const st = SERVICE_TYPES_BY_CODE[c]
    return {code: c, tip: st ? st.name + (st.note ? ' — ' + st.note : '') : c}
  })
  const overflow = codes.length > max ? {count: codes.length - max, rest: codes.slice(max).join(', ')} : null
  return {shown, overflow}
}

/* score → color/label ramp (mockup scoreColor/scoreLabel) */
export const scoreColor = (s) => (s < 40 ? '#C62828' : s <= 70 ? '#B45309' : s <= 90 ? '#43A047' : '#2E7D32')
export const scoreLabel = (s) =>
  s < 40 ? 'Incomplete' : s <= 70 ? 'Partially set up' : s <= 90 ? 'Mostly complete' : 'Ready'
